import PQueue from 'p-queue';
import { jpath } from 'jsonpath-mapper';
import { ContentType, Component, Project } from 'contensis-core-api';
import AssetsService from './AssetsService';
import BlocksService from './BlocksService';
import EntriesService from './EntriesService';
import ModelGenerationService from './ModelGenerationService';
import ModelsService from './ModelsService';
import NodesService from './NodesService';
import ProxiesService from './ProxiesService';
import RenderersService from './RenderersService';
import TagsService from './TagsService';
import WebhookSubscriptionService from './WebhookSubscriptionService';
import { MigrateRequest } from '~/models/Operations';
import ContensisManagementApi from '~/providers/ContensisManagementApi';
import ContensisRepository from '~/repositories/ContensisRepository';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import { mapCmsToManagementApiCtor as mapApiCreds } from '~/transformations/mapCmsToManagementApiCtor';
import { transformQueryForTarget } from '~/transformations/transformQueryForTargetRepo';
import { dedupe } from '~/util/array';
import { Logger } from '~/util/logger';
import { patchFetchForFileUploads } from '~/util/polyfills';
import { parseAlias } from '~/util/tryparse';

class MigratortronBaseService {
  // TODO: make this private
  sourceRepo: ContensisRepository;
  private syncRepos: {
    [projectId: string]: ContensisRepository;
  } = {};
  // TODO: make this private
  targetRepos: {
    [projectId: string]: ContensisRepository;
  } = {};
  ignoreErrors: boolean;
  isPreview: boolean;
  isHydrated = false;
  modeling!: ModelGenerationService;
  noCache: boolean;
  opName = 'migration';
  payload: MigrateRequest;
  queue: PQueue;
  readOnlyFields: boolean;

  transformGuids: boolean;
  zenQL?: string;
  errors: { [id: string]: MappedError[] } = {};

  get errorCount() {
    return Object.keys(this.errors).length;
  }
  // Provide readonly getters to access the repo objects
  get source() {
    return this.sourceRepo;
  }
  get sync() {
    return this.syncRepos;
  }
  get targets() {
    return this.targetRepos;
  }

  constructor(payload: MigrateRequest, preview: boolean) {
    patchFetchForFileUploads();
    const {
      source,
      target,
      concurrency,
      query,
      zenQL,
      entries,
      nodes,
      tagGroups,
      tags,
      webhooks,
      readOnlyFields,
      transformGuids,
      noCache,
      ignoreErrors,
    } = payload;

    this.ignoreErrors = ignoreErrors || false;
    this.noCache = noCache || false;
    this.isPreview = preview;
    this.payload = payload;
    this.queue = new PQueue({ concurrency: concurrency || 2 });
    this.readOnlyFields = readOnlyFields || false;
    this.transformGuids = transformGuids || false;
    this.zenQL = zenQL;

    if (this.payload.outputLogs) Logger.setLevel(this.payload.outputLogs);

    const sourceProjectId =
      source?.project ||
      (entries?.length
        ? // payload.entries might be our only source, find all project ids
          // supplied in here - this info is used for logging and responses
          dedupe(jpath('entries[*].sys.projectId', payload) || [])
            .sort()
            .join('~')
        : 'null');

    // Initialise sourceRepo and targetRepos
    const sourceApi = source
      ? new ContensisManagementApi(mapApiCreds(source, sourceProjectId))
      : undefined;

    // Initialise the source repo
    const contensisSource = new ContensisRepository(
      source as any,
      sourceProjectId,
      sourceApi as any,
      query,
      zenQL,
      false,
      payload.transformContentTypes
    );
    contensisSource.context.payload = payload;

    // Initialise all of the entity services for the source repo
    contensisSource.assets = new AssetsService(
      contensisSource,
      contensisSource
    );
    contensisSource.blocks = new BlocksService(
      contensisSource,
      contensisSource
    );
    contensisSource.entries = new EntriesService(
      contensisSource,
      contensisSource
    );
    contensisSource.models = new ModelsService(
      contensisSource,
      contensisSource
    );
    contensisSource.proxies = new ProxiesService(
      contensisSource,
      contensisSource
    );
    contensisSource.renderers = new RenderersService(
      contensisSource,
      contensisSource
    );
    contensisSource.tags = new TagsService(contensisSource, contensisSource);
    contensisSource.webhooks = new WebhookSubscriptionService(
      contensisSource,
      contensisSource
    );

    // Entries are supplied in the payload
    if (entries?.length) {
      contensisSource.entries.raw = entries;
      // contentTypeId, entryId, entryTitle, entryUri, filePath
      contensisSource.entryIds = entries.map((e) => [
        e.sys.contentTypeId || '',
        e.sys.id || '',
        e.entryTitle,
        e.sys.uri || '',
        (e.sys as any).properties?.filePath,
      ]);
    }

    contensisSource.nodes = new NodesService(contensisSource, contensisSource);

    // Nodes are supplied in the payload
    if (nodes?.length) {
      (contensisSource.nodes.raw as any) = nodes;
    }
    // Tag groups are supplied in the payload
    if (tagGroups?.length) {
      (contensisSource.tags.groups as any) = tagGroups;
    }
    // Tags are supplied in the payload
    if (tags?.length) {
      (contensisSource.tags.raw as any) = tags;
    }
    // Webhooks are supplied in the payload
    if (webhooks?.length) {
      (contensisSource.webhooks.subscriptions as any) = webhooks;
    }

    this.sourceRepo = contensisSource;

    // Initialise the target repos
    if (target) {
      const createRepo = (alias: string, project: string) => {
        // reuse the source repository when source and target project are the same
        return contensisSource.alias === alias &&
          project === sourceProjectId &&
          !this.payload.copyField
          ? contensisSource.repo
          : new ContensisManagementApi(mapApiCreds(target, project));
      };

      // Always read `latest` entries from the target;
      // partialEntries merge against `latest`, overriding any supplied versionStatus
      const targetQuery = {
        ...query,
        versionStatus:
          payload.partialEntries || !query?.versionStatus
            ? 'latest'
            : query.versionStatus,
      };

      const targetAlias = parseAlias(target.url);
      this.transformGuids =
        (!this.payload.copyField &&
          !this.payload.updateField &&
          this.source.alias === targetAlias) ||
        payload.transformGuids !== false;

      if (Array.isArray(target.targetProjects)) {
        for (const project of target.targetProjects) {
          // reuse the source repository when source and target project are the same
          const repo = createRepo(targetAlias, project);

          // query is transformed again later when it is used with GetTargetEntities
          // this plain query is used for deleting with a target set only
          this.targetRepos[project] = new ContensisRepository(
            target,
            project,
            repo as any,
            targetQuery,
            zenQL,
            this.transformGuids,
            payload.transformContentTypes
          );
          const targetRepo = this.targetRepos[project];
          targetRepo.context.payload = payload;

          // Initialise all of the entity services for each target repo
          targetRepo.assets = new AssetsService(targetRepo, this.source);
          targetRepo.blocks = new BlocksService(targetRepo, this.source);
          targetRepo.entries = new EntriesService(targetRepo, this.source);
          targetRepo.models = new ModelsService(targetRepo, this.source);
          targetRepo.proxies = new ProxiesService(targetRepo, this.source);
          targetRepo.renderers = new RenderersService(targetRepo, this.source);
          targetRepo.tags = new TagsService(targetRepo, this.source);
          targetRepo.nodes = new NodesService(targetRepo, this.source);
          targetRepo.webhooks = new WebhookSubscriptionService(
            targetRepo,
            this.source
          );
        }
      }

      if (Array.isArray(target.syncProjects)) {
        for (const project of target.syncProjects) {
          // reuse the target repository if one found
          if (this.targetRepos[project])
            this.syncRepos[project] = this.targetRepos[project];
          else {
            const repo = createRepo(targetAlias, project);

            this.syncRepos[project] = new ContensisRepository(
              target,
              project,
              repo,
              targetQuery,
              zenQL,
              this.transformGuids,
              payload.transformContentTypes
            );
            const syncRepo = this.syncRepos[project];
            syncRepo.context.payload = payload;

            syncRepo.entries = new EntriesService(syncRepo, this.source);
            syncRepo.nodes = new NodesService(syncRepo, this.source);
            syncRepo.tags = new TagsService(syncRepo, this.source);
          }
        }
      }
    }
  }

  /**
   * Get the project, components and content types from Management API
   * for all source and target repos, generating models and finding dependencies.
   * The returned lists are added to each repo and are not filtered by supplied ids as
   * the data will be required for comparison operations in model generation
   * and entries migration services which we also instantiate here and attach to each repo
   * effectively extending the usable scope of the repos.
   * @param ids ids of content types and/or components to filter the return values by
   * @param withModels setting this to false will not fetch and generate content models for target repos
   * @returns object containing arrays of components and contentTypes optionally filtered by supplied ids
   */
  HydrateContensisRepositories = async (
    assets: (string | ContentType | Component)[] = [],
    withModels = true
  ) => {
    const resourceIds = assets.map((m) => (typeof m === 'string' ? m : m.id));

    const isLoadingSuppliedAssets =
      assets.every((a) => typeof a !== 'string') && !!assets.length;

    if (this.payload.source || isLoadingSuppliedAssets) {
      const promises = [
        this.GetSourceRepo(
          resourceIds,
          isLoadingSuppliedAssets
            ? (assets as (ContentType | Component)[])
            : undefined
        ),
        this.GetTargetRepos(withModels),
      ];
      await Promise.all(promises);

      const { source } = this;
      for (const [projectId, contensis] of Object.entries(this.targets)) {
        if (
          (this.payload.copyField || this.payload.updateField) &&
          source.alias === contensis.alias &&
          source.projectId === projectId
        ) {
          // Share the assets from the source repo for copy field operations in
          // the same Contensis project
          const { project } = source.context;
          contensis.project = project as Project;
          contensis.context = source.context;
        }
      }

      const migrate = {
        components: source.context.resolve.components(resourceIds),
        contentTypes: source.context.resolve.contentTypes(resourceIds),
      };
      this.isHydrated = true;
      return migrate;
    } else {
      await this.GetTargetRepos(withModels);
      this.isHydrated = true;
    }
  };

  /**
   * Get all content types and components so we can get a complete picture
   * of the project
   * @param assets
   */
  GetSourceRepo = async (
    resourceIds?: string[],
    assets?: (ContentType | Component)[]
  ) => {
    const { source } = this;
    Logger.info(`Getting source models from '${source.projectId}'`);

    this.modeling = new ModelGenerationService(source, this.payload, true);

    const context = await this.modeling.GetContensisContext(
      resourceIds,
      assets
    );

    // TODO: retire these props in favour of calling context.*
    source.project =
      context.project ||
      ({
        primaryLanguage: 'en-GB',
        id: 'null',
      } as Project);
    source.context = context;

    // Create new instances of Assets and Entries services
    // as we have everything from the modeling stage in context
    source.assets = new AssetsService(source, source);

    const modelEntries = source.entries;
    source.entries = new EntriesService(source, source);
    // retain any entry dependencies found during modeling
    source.entries.dependencies = modelEntries.dependencies;
  };

  GetTargetRepos = async (withModels = true) => {
    const { source } = this;
    for (const [projectId, contensis] of Object.entries(this.targetRepos)) {
      if (
        (this.payload.copyField || this.payload.updateField) &&
        source.alias === contensis.alias &&
        source.projectId === contensis.projectId
      )
        break;
      Logger.info(`Getting target repo data from '${projectId}'`);

      if (!withModels) {
        const project = await contensis.repo.GetProject();
        contensis.project = project;
      } else {
        const modelService = new ModelGenerationService(
          contensis,
          // TODO: remove payload from here and use repo.context.payload instead
          this.payload,
          false
        );
        const context = await modelService.GetContensisContext();

        // TODO: retire these props in favour of calling context.*
        contensis.project = context.project as Project;

        contensis.context = context;
        contensis.context.source = source;
      }
      contensis.assets = new AssetsService(contensis, source);
      contensis.entries = new EntriesService(contensis, source);
      // query is not transformed until it is used with GetTargetRepos
      // plain query is used for deleting at targetProjects
      contensis.query = transformQueryForTarget(contensis);
    }

    for (const contensis of Object.values(this.sync)) {
      // query is not transformed until it is used with GetTargetRepos
      // plain query is used for deleting at targetProjects
      contensis.query = transformQueryForTarget(contensis);
    }
  };

  AddError = (id: string, error: AppError | undefined, arr = this.errors) => {
    const mappedError = mapErrorToObject(error);
    if (!arr[id]) arr[id] = [];
    arr[id].push(mappedError);
    return mappedError;
  };
}

export default MigratortronBaseService;
